"use client";

import React, { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { activeCtx, dataCtx, dictionary, staticData, user_ref } from "@/lib/api/state";

export interface ActiveContextState {
    ctx: string[];
    refresh: boolean;
    properties: Record<string, unknown>;
    region?: string;
}

export interface UserState {
    name: string;
    permissions: string[];
    role?: string;
    theme?: string;
    lang?: string;
    avatar?: string;
    firstName?: string;
    lastName?: string;
    loaded: boolean;
}

export interface EditorContextState {
    props: { idx: number; elements: unknown[] };
}

export interface LayoutConfig {
    ripple: boolean;
    inputStyle: "outlined" | "filled";
    menuMode: "static" | "overlay";
    colorScheme: "light" | "dark";
    theme: string;
    scale: number;
}

export interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    profileSidebarVisible: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
}

interface LayoutContextValue {
    layoutConfig: LayoutConfig;
    setLayoutConfig: React.Dispatch<React.SetStateAction<LayoutConfig>>;
    layoutState: LayoutState;
    setLayoutState: React.Dispatch<React.SetStateAction<LayoutState>>;
    onMenuToggle: () => void;
    showProfileSidebar: () => void;
    activeContext: ActiveContextState;
    setActiveContext: React.Dispatch<React.SetStateAction<ActiveContextState>>;
    enableDashboards: boolean;
    setEnableDashboards: React.Dispatch<React.SetStateAction<boolean>>;
    refreshContext: boolean;
    setRefreshContext: React.Dispatch<React.SetStateAction<boolean>>;
    regionChange: boolean;
    setRegionChange: React.Dispatch<React.SetStateAction<boolean>>;
    userState: UserState;
    setUserState: React.Dispatch<React.SetStateAction<UserState>>;
    editEnabled: boolean;
    setEditEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    editorContext: EditorContextState;
    setEditorContext: React.Dispatch<React.SetStateAction<EditorContextState>>;
    selectedElement: number;
    setSelectedElement: React.Dispatch<React.SetStateAction<number>>;
    refreshDashboards: boolean;
    setRefreshDashboards: React.Dispatch<React.SetStateAction<boolean>>;
    dataContext: { path?: unknown; lang: string };
    setDataContext: React.Dispatch<React.SetStateAction<{ path?: unknown; lang: string }>>;
    language: Record<string, unknown> & { isLoaded?: boolean };
    setLanguage: React.Dispatch<React.SetStateAction<Record<string, unknown> & { isLoaded?: boolean }>>;
    menuData: unknown[];
    setMenuData: React.Dispatch<React.SetStateAction<unknown[]>>;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export const useLayout = () => {
    const ctx = useContext(LayoutContext);
    if (!ctx) throw new Error("useLayout must be used within LayoutProvider");
    return ctx;
};

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
    const [refreshContext, setRefreshContext] = useState(false);
    const [activeContext, setActiveContext] = useState<ActiveContextState>({ ctx: [], refresh: false, properties: {} });
    const [enableDashboards, setEnableDashboards] = useState(false);
    const [refreshDashboards, setRefreshDashboards] = useState(false);
    const [userState, setUserState] = useState<UserState>({ name: "Loading", permissions: [], loaded: false });
    const [editEnabled, setEditEnabled] = useState(false);
    const [editorContext, setEditorContext] = useState<EditorContextState>({ props: { idx: 0, elements: [] } });
    const [selectedElement, setSelectedElement] = useState(0);
    const [dataContext, setDataContext] = useState<{ path?: unknown; lang: string }>({ path: {}, lang: "EN-US" });
    const [language, setLanguage] = useState<Record<string, unknown> & { isLoaded?: boolean }>({ isLoaded: false });
    const [regionChange, setRegionChange] = useState(false);
    const [menuData, setMenuData] = useState<unknown[]>([]);

    const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
        ripple: true,
        inputStyle: "outlined",
        menuMode: "static",
        colorScheme: "light",
        theme: "slate",
        scale: 1,
    });

    const [layoutState, setLayoutState] = useState<LayoutState>({
        staticMenuDesktopInactive: !enableDashboards,
        overlayMenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
    });

    /* Sync module-level singletons */
    useEffect(() => {
        dictionary.isLoaded = !!language.isLoaded;
        Object.assign(dictionary, language);
    }, [language]);

    useEffect(() => {
        dataCtx.path = dataContext.path;
        dataCtx.lang = dataContext.lang;
    }, [dataContext]);

    useEffect(() => {
        activeCtx.ctx = activeContext.ctx;
        activeCtx.properties = activeContext.properties;
        activeCtx.region = activeContext.region;
        activeCtx.refresh = activeContext.refresh;
    }, [activeContext]);

    useEffect(() => {
        staticData.editEnabled = editEnabled;
    }, [editEnabled]);

    useEffect(() => {
        user_ref.user = { permissions: userState.permissions, role: userState.role, theme: userState.theme, lang: userState.lang, name: userState.name };
    }, [userState]);

    const isOverlay = () => layoutConfig.menuMode === "overlay";
    const isDesktop = () => typeof window !== "undefined" && window.innerWidth > 691;

    const onMenuToggle = () => {
        if (isOverlay()) {
            setLayoutState((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
        }
        if (isDesktop()) {
            setLayoutState((prev) => ({ ...prev, staticMenuDesktopInactive: !prev.staticMenuDesktopInactive }));
        } else {
            setLayoutState((prev) => ({ ...prev, staticMenuMobileActive: !prev.staticMenuMobileActive }));
        }
    };

    const showProfileSidebar = () => {
        setLayoutState((prev) => ({ ...prev, profileSidebarVisible: !prev.profileSidebarVisible && enableDashboards }));
    };

    const value: LayoutContextValue = {
        layoutConfig, setLayoutConfig,
        layoutState, setLayoutState,
        onMenuToggle, showProfileSidebar,
        activeContext, setActiveContext,
        enableDashboards, setEnableDashboards,
        refreshContext, setRefreshContext,
        regionChange, setRegionChange,
        userState, setUserState,
        editEnabled, setEditEnabled,
        editorContext, setEditorContext,
        selectedElement, setSelectedElement,
        refreshDashboards, setRefreshDashboards,
        dataContext, setDataContext,
        language, setLanguage,
        menuData, setMenuData,
    };

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};
